import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import { NAV_LINKS } from "../data/site";
import { getLenis, scrollToId } from "../lib/scroll";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const lenis = getLenis();
    if (!lenis) return;
    if (open) lenis.stop();
    else lenis.start();
  }, [open]);

  const go = (id) => {
    const lenis = getLenis();
    if (lenis && lenis.isStopped) lenis.start();
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-40 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-[#0c1220]/95 backdrop-blur-md border-b border-white/[0.06]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 sm:h-20 max-w-[1240px] items-center justify-between px-5 sm:px-8">
          <button
            data-testid="logo-home-button"
            onClick={() => go("home")}
            aria-label="Phade Barbers — back to top"
            className="shrink-0"
          >
            <img
              src="/assets/logo.png"
              alt="Phade Barbers logo"
              className="h-9 sm:h-11 w-auto rounded-md"
            />
          </button>

          <nav aria-label="Primary" className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                data-testid={`nav-link-${link.id}`}
                onClick={() => go(link.id)}
                className="group relative text-[13px] font-semibold tracking-[0.14em] text-white/75 transition-colors duration-300 hover:text-white"
              >
                {link.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#9fd3f0] transition-[width] duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="book-a-cut-button"
              onClick={() => go("barbers")}
              className="group hidden sm:inline-flex items-center gap-2 rounded-lg bg-[#4a9bdc] px-6 py-2.5 text-[13px] font-bold tracking-[0.1em] text-white transition-colors duration-300 hover:bg-[#5aaae8]"
            >
              BOOK A CUT
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className="lg:hidden rounded-lg border border-white/15 p-2.5 text-white"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex flex-col bg-[#0c1220]"
          >
            <div className="flex h-16 items-center justify-between px-5">
              <img src="/assets/logo.png" alt="Phade Barbers logo" className="h-9 w-auto rounded-md" />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="rounded-lg border border-white/15 p-2.5 text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav aria-label="Mobile" className="flex flex-1 flex-col justify-center gap-2 px-8">
              {NAV_LINKS.map((link, i) => (
                <motion.button
                  key={link.id}
                  data-testid={`mobile-nav-link-${link.id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => go(link.id)}
                  className="border-b border-white/[0.07] py-4 text-left text-3xl font-black tracking-tight text-white transition-colors hover:text-[#9fd3f0]"
                >
                  {link.label}
                </motion.button>
              ))}
              <motion.button
                data-testid="mobile-book-a-cut-button"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                onClick={() => go("barbers")}
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-lg bg-[#4a9bdc] px-6 py-4 text-sm font-bold tracking-[0.1em] text-white"
              >
                BOOK A CUT
                <ArrowRight className="h-4 w-4" />
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
